import React from 'react';
import { cn } from '../../lib/utils';

export interface TableColumn<T> {
  /** Column key matching data object keys */
  key: keyof T;
  /** Column header text */
  header: string;
  /** Custom render function for cell content */
  render?: (value: T[keyof T], row: T) => React.ReactNode;
}

export interface TableProps<T> {
  /** Column definitions */
  columns: TableColumn<T>[];
  /** Table data rows */
  data: T[];
  /** Message shown when data is empty */
  emptyMessage?: string;
  /** Callback when row is clicked */
  onRowClick?: (row: T) => void;
  /** Render function for actions column */
  renderActions?: (row: T) => React.ReactNode;
  /** Custom className for table */
  className?: string;
}

/**
 * Table component for displaying tabular data
 *
 * @example
 * ```tsx
 * const columns = [
 *   { key: 'name', header: 'Name' },
 *   { key: 'email', header: 'Email' },
 *   { key: 'status', header: 'Status', render: (val) => <Badge>{val}</Badge> }
 * ];
 *
 * <Table
 *   columns={columns}
 *   data={users}
 *   onRowClick={handleRowClick}
 *   renderActions={(row) => <Button onClick={() => edit(row)}>Edit</Button>}
 * />
 * ```
 */
export function Table<T>({
  columns,
  data,
  emptyMessage = 'No data available.',
  onRowClick,
  renderActions,
  className,
}: TableProps<T>) {
  return (
    <div className="overflow-x-auto">
      <table className={cn('w-full border-collapse', className)} role="table">
        <thead>
          <tr className="border-b bg-gray-50">
            {columns.map((column) => (
              <th
                key={String(column.key)}
                className="px-4 py-2 text-left text-sm font-semibold text-gray-900"
              >
                {column.header}
              </th>
            ))}
            {renderActions && (
              <th className="px-4 py-3 text-left text-sm font-semibold text-gray-900">
                Actions
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td
                colSpan={columns.length + (renderActions ? 1 : 0)}
                className="px-4 py-8 text-center text-gray-500"
              >
                {emptyMessage}
              </td>
            </tr>
          ) : (
            data.map((row, index) => (
              <tr
                key={index}
                className={cn(
                  'border-b',
                  onRowClick && 'hover:bg-gray-50 cursor-pointer'
                )}
                onClick={() => onRowClick?.(row)}
              >
                {columns.map((column) => {
                  const value = row[column.key];
                  return (
                    <td
                      key={String(column.key)}
                      className="px-4 py-2 text-sm text-gray-900 border-b"
                    >
                      {column.render ? column.render(value, row) : String(value)}
                    </td>
                  );
                })}
                {renderActions && (
                  <td className="px-4 py-2 text-sm border-b">
                    {renderActions(row)}
                  </td>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

Table.displayName = 'Table';
